import { Dispatch, SetStateAction } from 'react'

export type ID = undefined | null | number
export type FKID = undefined | number

export type PaginationState = {
  page: number
  items_per_page: 10 | 30 | 50 | 100
  links?: Array<{ label: string; active: boolean; url: string | null; page: number | null }>
  total: number
  customer: number
  person: number
  employee: number
  complete: number
  halfComplete: number
  incomplete: number
}

export type SortState = {
    sort?: string
    order?: 'asc' | 'desc'
}

export type FilterState = {
    filter?: unknown
}

export type SearchState = {
    search?: string
}

export type Response<T> = {
    data?: T
    payload?: {
        message?: string
        errors?: {
            [key: string]: Array<string>
        }
        pagination?: PaginationState
    }
}

export type QueryState = PaginationState & SortState & FilterState & SearchState

export type QueryRequestContextProps = {
    state: QueryState
    updateState: (updates: Partial<QueryState>) => void
}

export const initialQueryState: QueryState = {
  page: 1,
  items_per_page: 10,
  total: 0,
  customer: 0,
  person: 0,
  employee: 0,
  complete: 0,
  halfComplete: 0,
  incomplete: 0
}

export const initialQueryRequest: QueryRequestContextProps = {
    state: initialQueryState,
    updateState: () => { },
}

export type QueryResponseContextProps<T> = {
    response?: Response<Array<T>> | undefined
    refetch: () => void
    isLoading: boolean
    query: string
}

export const initialQueryResponse = { refetch: () => { }, isLoading: false, query: '' }

export type ListViewContextProps = {
    selected: Array<ID>
    onSelect: (selectedId: ID) => void
    onSelectAll: () => void
    clearSelected: () => void
    // NULL => (CREATION MODE) | MODAL IS OPENED
    // NUMBER => (EDIT MODE) | MODAL IS OPENED
    // UNDEFINED => MODAL IS CLOSED
    itemIdForUpdate?: ID
    itemIdForSecondOperation?: ID
    itemIdForThirdOperation?: ID
    itemIdForFourthOperation?: ID
    itemStartDateForUpdate?: Date
    itemEndDateForUpdate?: Date
    itemStartDateForSecondOperation?: Date
    itemEndDateForSecondOperation?: Date
    itemStartDateForThirdOperation?: Date
    itemEndDateForThirdOperation?: Date
    itemCustomerIdForUpdate?: FKID
    itemCustomerAssignmentIdForUpdate?: FKID
    itemEmployeeIdForUpdate?: FKID
    itemUnitIdForUpdate?: FKID
    itemLessonIdForUpdate?: FKID
    itemUserIdForUpdate?: FKID
    itemRoleIdForUpdate?: FKID
    itemCourseIdForUpdate?: FKID
    setItemIdForUpdate: Dispatch<SetStateAction<ID>>
    setItemIdForSecondOperation?: Dispatch<SetStateAction<ID>>
    setItemIdForThirdOperation?: Dispatch<SetStateAction<ID>>
    setItemIdForFourthOperation?: Dispatch<SetStateAction<ID>>
    setItemStartDateForUpdate?: Dispatch<SetStateAction<Date>>
    setItemEndDateForUpdate?: Dispatch<SetStateAction<Date>>
    setItemStartDateForSecondOperation?: Dispatch<SetStateAction<Date>>
    setItemEndDateForSecondOperation?: Dispatch<SetStateAction<Date>>
    setItemStartDateForThirdOperation?: Dispatch<SetStateAction<Date>>
    setItemEndDateForThirdOperation?: Dispatch<SetStateAction<Date>>
    setItemAssignmentIdForUpdate?: Dispatch<SetStateAction<FKID>>
    setItemCustomerIdForUpdate?: Dispatch<SetStateAction<FKID>>
    setItemEmployeeIdForUpdate?: Dispatch<SetStateAction<FKID>>
    setItemExpenseTypeIdForUpdate?: Dispatch<SetStateAction<FKID>>
    setItemUnitIdForUpdate?: Dispatch<SetStateAction<FKID>>
    setItemEmployeeTypeIdForUpdate?: Dispatch<SetStateAction<FKID>>
    setItemWorkingStatueIdForUpdate?: Dispatch<SetStateAction<FKID>>
    setItemLessonIdForUpdate?: Dispatch<SetStateAction<FKID>>
    setItemUserIdForUpdate?: Dispatch<SetStateAction<FKID>>
    setItemRoleIdForUpdate?: Dispatch<SetStateAction<FKID>>
    setItemCourseIdForUpdate?: Dispatch<SetStateAction<FKID>>
    isAllSelected: boolean
    disabled: boolean
}

export const initialListView: ListViewContextProps = {
    selected: [],
    onSelect: () => { },
    onSelectAll: () => { },
    clearSelected: () => { },
    setItemIdForUpdate: () => { },
    setItemIdForSecondOperation: () => { },
    setItemIdForThirdOperation: () => { },
    setItemIdForFourthOperation: () => { },
    setItemStartDateForUpdate: () => { },
    setItemEndDateForUpdate: () => { },
    setItemStartDateForSecondOperation: () => { },
    setItemEndDateForSecondOperation: () => { },
    setItemStartDateForThirdOperation: () => { },
    setItemEndDateForThirdOperation: () => { },
    setItemCustomerIdForUpdate: () => { },
    setItemAssignmentIdForUpdate: () => { },
    setItemEmployeeIdForUpdate: () => { },
    setItemUnitIdForUpdate: () => { },
    setItemLessonIdForUpdate: () => { },
    setItemUserIdForUpdate: () => { },
    setItemRoleIdForUpdate: () => { },
    isAllSelected: false,
    disabled: false,
}

export type EntityTypeOption = {
    id: ID,
    name: string
}